import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Euro, Activity, ClipboardList, RefreshCw, AlertCircle } from "lucide-react";
import { apiAdminOverview, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Owner Overview — KPI dashboard.
 *
 * Renders whatever `groups` the backend returns. To add a new KPI you only
 * need to extend the `_build_overview()` helper in /app/backend/admin.py.
 * This component does NOT hard-code any KPI ID.
 */
const GROUP_ICONS = {
  members: Users,
  revenue: Euro,
  activity: Activity,
  workload: ClipboardList,
};

const STATUS_COLORS = {
  green: { dot: "#00FF66", glow: "rgba(0,255,102,0.08)", label: "Normal" },
  yellow: { dot: "#FFB000", glow: "rgba(255,176,0,0.10)", label: "Needs attention" },
  red: { dot: "#FF4F4F", glow: "rgba(255,79,79,0.12)", label: "Action required" },
};

function formatValue(value, fmt) {
  if (value === null || value === undefined) return "—";
  if (fmt === "currency_eur") {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(Number(value));
  }
  return new Intl.NumberFormat("de-DE").format(Number(value));
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function OwnerOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiAdminOverview();
      setData(d);
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <OverviewSkeleton />;
  }

  if (error && !data) {
    return (
      <div
        className="mt-6 border border-[#3a2222] bg-[#1a0e0e] p-5 flex items-center gap-3"
        data-testid="owner-overview-error"
      >
        <AlertCircle className="w-5 h-5 text-[#FF4F4F]" />
        <div className="flex-1">
          <div className="font-bold text-white text-sm">Could not load Owner Overview</div>
          <div className="text-xs text-neutral-400 mt-0.5">{error}</div>
        </div>
        <button
          onClick={load}
          className="border border-neutral-800 hover:border-[#FF4F4F] text-xs font-mono uppercase tracking-[0.2em] text-neutral-300 px-3 py-2"
          data-testid="owner-overview-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="mt-6 border border-neutral-900 bg-[#0a0a0a]"
      data-testid="owner-overview"
    >
      <header className="px-5 py-3 border-b border-neutral-900 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF]">
          owner overview
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">
          {data?.generated_at && (
            <span data-testid="owner-overview-generated">
              updated {relativeTime(data.generated_at)}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="owner-overview-refresh"
            className="inline-flex items-center gap-1.5 border border-neutral-800 hover:border-neutral-600 text-neutral-300 hover:text-white px-2.5 py-1.5 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            refresh
          </button>
        </div>
      </header>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {data?.groups?.map((group) => (
          <KpiGroup key={group.id} group={group} />
        ))}
      </div>
    </motion.section>
  );
}

function KpiGroup({ group }) {
  const Icon = GROUP_ICONS[group.id] || Activity;
  return (
    <div data-testid={`owner-overview-group-${group.id}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2.5">
        <Icon className="w-3.5 h-3.5" />
        {group.label}
      </div>
      <div className="grid grid-cols-1 gap-2">
        {group.kpis.map((k) => (
          <KpiCard key={k.id} kpi={k} />
        ))}
      </div>
    </div>
  );
}

function KpiCard({ kpi }) {
  const statusInfo = kpi.status ? STATUS_COLORS[kpi.status] : null;
  return (
    <div
      data-testid={`kpi-${kpi.id}`}
      data-kpi-status={kpi.status || "none"}
      className="relative border border-neutral-900 bg-neutral-950 px-4 py-3 transition-colors hover:border-neutral-700"
      style={statusInfo ? { boxShadow: `inset 0 0 0 1px ${statusInfo.glow}` } : undefined}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span
          className="font-mono text-2xl font-bold text-white tabular-nums"
          data-testid={`kpi-${kpi.id}-value`}
        >
          {formatValue(kpi.value, kpi.format)}
        </span>
        {statusInfo && (
          <span
            className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.25em]"
            style={{ color: statusInfo.dot }}
            title={statusInfo.label}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: statusInfo.dot }}
            />
            {kpi.status}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-[11px] text-neutral-400 uppercase tracking-wider">
        {kpi.label}
      </div>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="mt-6 border border-neutral-900 bg-[#0a0a0a]" data-testid="owner-overview-loading">
      <div className="px-5 py-3 border-b border-neutral-900">
        <div className="h-3 w-32 bg-neutral-900 animate-pulse" />
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, gi) => (
          <div key={gi}>
            <div className="h-3 w-24 bg-neutral-900 mb-2.5 animate-pulse" />
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((__, ki) => (
                <div key={ki} className="border border-neutral-900 bg-neutral-950 px-4 py-3">
                  <div className="h-7 w-16 bg-neutral-900 animate-pulse" />
                  <div className="h-2 w-24 bg-neutral-900 mt-2 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
